import type { EntitiesHallAdditionalDataModel } from '@cw-elements/mfe-setup/util';
import { EntityTypeLowerCaseEnum } from '@cw-elements/mfe-setup/util';
import { ReactComponent as SvgRiskManagement } from 'assets/images/risk-management.svg';
import { useHasRiskManagementAccess } from 'core/utils/hooks/use-has-risk-management-access.hook';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import { getCurrentWorkspaceId } from 'entities/company/company.selectors';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styles from './use-risk-management-entry.module.css';

const SEGMENT_EVENT = 'Risk Management - Opened from Entity Selector';

type RiskManagementEntry = (id: string, entityType: EntityTypeLowerCaseEnum) => EntitiesHallAdditionalDataModel | null;

interface RiskManagementEntryProps {
  entitiesSelectorAdditionalData?: RiskManagementEntry;
  entitiesSelectorOnNavigate: (selectedEntities: { workspaceId: string | null }) => void;
}

export const useRiskManagementEntry = (): RiskManagementEntryProps => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { track } = useSegmentTracking();
  const hasRiskManagementAccess = useHasRiskManagementAccess();
  const currentWorkspaceId = useSelector(getCurrentWorkspaceId);
  const [browsedWorkspaceId, setBrowsedWorkspaceId] = useState<string | null>(null);

  const entitiesSelectorOnNavigate = useCallback(({ workspaceId }: { workspaceId: string | null }) => {
    setBrowsedWorkspaceId(workspaceId);
  }, []);

  const isBrowsingCurrentWorkspace = browsedWorkspaceId === null || browsedWorkspaceId === currentWorkspaceId;

  const buildEntry = useCallback<RiskManagementEntry>(
    (_id, entityType) => {
      if (entityType !== EntityTypeLowerCaseEnum.ORGANIZATION || !isBrowsingCurrentWorkspace) return null;

      return {
        data: '',
        tooltip: t('pages.regional_overview.title'),
        icon: <SvgRiskManagement width={18} height={18} className={styles.riskManagementIcon} />,
        onClick: (organizationId: string) => {
          track(SEGMENT_EVENT);
          navigate(`/company/${organizationId}/regional-overview`);
        }
      };
    },
    [t, navigate, track, isBrowsingCurrentWorkspace]
  );

  return {
    entitiesSelectorAdditionalData: hasRiskManagementAccess ? buildEntry : undefined,
    entitiesSelectorOnNavigate
  };
};
